/*
 * Copyright (c) 2008-present Sonatype, Inc.
 *
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/pro/attributions
 * Sonatype and Sonatype Nexus are trademarks of Sonatype, Inc. Apache Maven is a trademark of the Apache Foundation.
 * M2Eclipse is a trademark of the Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import React from 'react';
import {useMachine} from '@xstate/react';

import {faChevronRight, faInfoCircle, faCopy} from '@fortawesome/free-solid-svg-icons';

import {
  ContentBody,
  HelpTile,
  NxButton,
  NxFontAwesomeIcon,
  NxTable,
  NxTableBody,
  NxTableCell,
  NxTableHead,
  NxTableRow,
  Page,
  PageActions,
  PageHeader,
  PageTitle,
  Section,
  SectionToolbar,
  Utils
} from '@sonatype/nexus-ui-plugin';

import ReplicationListMachine from './ReplicationListMachine';

import UIStrings from '../../../../constants/UIStrings';

const {REPLICATION} = UIStrings;

export default function ReplicationList({onCreate, onEdit}) {
  const [current, send] = useMachine(ReplicationListMachine, {devTools: true});
  const isLoading = current.matches('loading');
  const data = current.context.data;
  const error = current.context.error;

  function getSortDirection(fieldName, {sortField, sortDirection}) {
    if (sortField !== fieldName) {
      return null;
    }
    return sortDirection === Utils.ASC ? 'asc' : 'desc';
  }

  const nameSortDir = getSortDirection('name', current.context);
  const sourceRepoSortDir = getSortDirection('sourceRepositoryName', current.context);
  const targetInstanceSortDir = getSortDirection('destinationInstanceUrl', current.context);
  const destinationRepoSortDir = getSortDirection('destinationRepositoryName', current.context);

  return <Page className="nxrm-replication">
    <PageHeader>
      <PageTitle icon={faCopy} {...REPLICATION.MENU}/>
      <PageActions>
        <NxButton variant="primary" onClick={onCreate}>New Replication</NxButton>
      </PageActions>
    </PageHeader>
    <ContentBody className="nxrm-replication-list">
      <h2 className="nx-h2">
        {REPLICATION.LIST.HEADING}
      </h2>
      <NxTable>
        <NxTableHead>
          <NxTableRow>
            <NxTableCell
                onClick={() => send('SORT_BY_NAME')}
                isSortable
                sortDir={nameSortDir}
            >
              {REPLICATION.LIST.NAME_LABEL}
            </NxTableCell>
            <NxTableCell
                onClick={() => send('SORT_BY_SOURCE_REPO')}
                isSortable
                sortDir={sourceRepoSortDir}
            >
              {REPLICATION.LIST.SOURCE_REPO_LABEL}
            </NxTableCell>
            <NxTableCell
                onClick={() => send('SORT_BY_TARGET_INSTANCE')}
                isSortable
                sortDir={targetInstanceSortDir}
            >
              {REPLICATION.LIST.TARGET_INSTANCE_LABEL}
            </NxTableCell>
            <NxTableCell
                onClick={() => send('SORT_BY_DESTINATION_REPO')}
                isSortable
                sortDir={destinationRepoSortDir}
            >
              {REPLICATION.LIST.DESTINATION_REPO_LABEL}
            </NxTableCell>
            <NxTableCell hasIcon/>
          </NxTableRow>
        </NxTableHead>
        <NxTableBody
            isLoading={isLoading}
            error={error}
            emptyMessage={REPLICATION.LIST.EMPTY_LIST}>
          {data.map(({name, sourceRepositoryName, destinationInstanceUrl, destinationRepositoryName}) => (
              <NxTableRow key={sourceRepositoryName} isClickable onClick={() => onEdit(name)}>
                <NxTableCell>{name}</NxTableCell>
                <NxTableCell>{sourceRepositoryName}</NxTableCell>
                <NxTableCell>{destinationInstanceUrl}</NxTableCell>
                <NxTableCell>{destinationRepositoryName}</NxTableCell>
                <NxTableCell hasIcon><NxFontAwesomeIcon icon={faChevronRight}/></NxTableCell>
              </NxTableRow>
          ))}
        </NxTableBody>
      </NxTable>

      <HelpTile>
        <h3><NxFontAwesomeIcon icon={faInfoCircle}/>{REPLICATION.LIST.HELP_TITLE}</h3>
        <p dangerouslySetInnerHTML={{__html: REPLICATION.LIST.HELP_TEXT}}/>
      </HelpTile>
    </ContentBody>
  </Page>;
}
